/**
 * Recent Records
 * Displays most recently published records
 */
import { useTheme } from '@mui/material/styles';
import { Link } from 'gatsby';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader/ContentLoader';
import MenuItem from '@components/MenuItem';
import NullStateDiv from '@components/NullStateDiv';

import * as SURVEYS from '@constants/survey-ids';

import { useGetMostRecentRecords } from '@hooks/api/patient-record';

import { resolveIcon } from '@utils/resolveIcon';

import { DashHeader } from '../Common.style';
import { MenuItemContainer } from './RecentRecords.style';

const messages = defineMessages({
  recentRecords: {
    id: 'routinesPage.recentRecords',
    defaultMessage: 'Recent Records',
  },
  noRecords: {
    id: 'routinesPage.noRecords',
    defaultMessage: 'No Known Records',
  },
});

const RECORD_TYPE_ID_MAP: Record<string, string> = {
  Appointments: SURVEYS.APPOINTMENTS_ID,
  Surgeries: SURVEYS.SURGERY_ID,
  'Emergency Visits': SURVEYS.EMERGENCY_VISIT_ID,
  Hospitalizations: SURVEYS.HOSPITALIZATION_ID,
  'Imaging & Tests': SURVEYS.IMAGING_TESTS_ID,
  'Genetic Informations': SURVEYS.CARRIER_STATUS_ID,
  'Medical Conditions': SURVEYS.MEDICAL_CONDITION_ID,
  'Vital Signs': SURVEYS.VITAL_SIGN_ID,
  Medications: SURVEYS.MEDICATION_ID,
  Allergies: SURVEYS.ALLERGY_ID,
  Immunizations: SURVEYS.IMMUNIZATION_ID,
  'Lab Reports': SURVEYS.LAB_RESULTS_ID,
  'Family History': SURVEYS.FAMILY_HISTORY_ID,
  'Body Observations': SURVEYS.BODY_ID,
  'Sleep Observations': SURVEYS.SLEEP_ID,
  'Fitness Observations': SURVEYS.FITNESS_ID,
};

const RecentRecords: React.FC = () => {
  const { formatMessage } = useIntl();
  const { data, loading } = useGetMostRecentRecords();
  const theme = useTheme();

  const records = data?.getMostRecentRecords ?? [];

  return (
    <div>
      <DashHeader>{formatMessage(messages.recentRecords)}</DashHeader>
      <ContentLoader loading={loading}>
        {records.length > 0 && (
          <MenuItemContainer>
            {records.map((record) => {
              const recordType = RECORD_TYPE_ID_MAP[record.recordType ?? ''];
              return (
                <Link
                  key={`recent-record-${record.resource_model_id}`}
                  to={record.route ?? ''}
                >
                  <MenuItem
                    text={record.displayName ?? undefined}
                    {...resolveIcon(recordType, theme)}
                  />
                </Link>
              );
            })}
          </MenuItemContainer>
        )}
        {records.length === 0 && (
          <NullStateDiv>{formatMessage(messages.noRecords)}</NullStateDiv>
        )}
      </ContentLoader>
    </div>
  );
};

export default withErrorBoundary(RecentRecords, {
  fallback: null,
});
